import { Config, PS } from "./client-main";
import { extractionAudioEmbed, extractionAudioRoom, extractionAudioAllows, installExtractionAudio } from "./extraction-audio"; // cherish-extraction-audio

// cherish-battle-volume: 배틀 음량 기준선. 슬라이더 값에 이 배수를 곱한다.
// 배틀 사운드가 통화·다른 탭보다 너무 컸다 (2026-09-03). 1 로 두면 상류와 동일.
const CHERISH_BGM_SCALE = 0.25;
const CHERISH_EFFECT_SCALE = 0.7;

export class BattleBGM {
	/**
	 * May be shared with other BGM objects: every battle has its own BattleBGM
	 * object, but two battles with the same music will have the same HTMLAudioElement
	 * object.
	 */
	sound?: HTMLAudioElement;
	url: string;
	timer: number | undefined = undefined;
	loopstart: number;
	loopend: number;
	/**
	 * When multiple battles with BGM are open, they will be `isPlaying`, but only the
	 * first one will be `isActuallyPlaying`. In addition, muting volume or setting
	 * BGM volume to 0 will set `isActuallyPlaying` to false.
	 */
	isPlaying = false;
	isActuallyPlaying = false;
	/**
	 * The sound should be rewound when it next plays.
	 */
	willRewind = true;
	constructor(url: string, loopstart: number, loopend: number) {
		this.url = url;
		this.loopstart = loopstart;
		this.loopend = loopend;
	}
	play() {
		this.willRewind = true;
		this.resume();
	}
	resume() {
		this.isPlaying = true;
		// cherish-bgm-single: 현재 곡만 재생하고 나머지는 멈춘다 (상류는 pause 쪽만 update 를 불렀다)
		BattleBGM.update();
	}
	pause() {
		this.isPlaying = false;
		this.actuallyPause();
		BattleBGM.update();
	}
	stop() {
		this.pause();
		this.willRewind = true;
	}
	destroy() {
		BattleSound.deleteBgm(this);
		this.pause();
	}

	actuallyResume() {
		if (this !== BattleSound.currentBgm()) return;

		if (!this.sound) this.sound = BattleSound.getSound(this.url);
		if (!this.sound) return;
		// cherish-bgm-volume: 볼륨 동기화를 isActuallyPlaying 조기 반환보다 앞으로 옮겼다.
		// 원본은 재생 중이면 바로 return 해서 <audio>.volume 을 다시 쓰지 않았고,
		// 그래서 음악이 나오는 중에 음량 슬라이더를 움직여도 크기가 안 변했다.
		this.sound.volume = BattleSound.bgmVolume / 100 * CHERISH_BGM_SCALE; // cherish-battle-volume
		if (this.isActuallyPlaying) return;

		if (this.willRewind) this.sound.currentTime = 0;
		this.willRewind = false;
		this.isActuallyPlaying = true;
		BattleSound.claimBgmTab?.(); // cherish-bgm-single: 다른 탭은 양보
		// cherish-bgm-autoplay: 링크로 바로 열린 새 탭(관전)은 사용자 조작이 없어 자동재생이
		// 거부된다. 거부되면 「재생 중」 표시를 되돌리고 첫 클릭·키 입력 때 다시 시도한다.
		const played = this.sound.play() as unknown as Promise<void> | undefined;
		if (played && typeof played.catch === 'function') {
			played.catch(() => {
				if (!this.isActuallyPlaying) return;
				this.isActuallyPlaying = false;
				BattleSound.retryBgmOnGesture();
			});
		}
		this.updateTime();
	}
	actuallyPause() {
		if (!this.isActuallyPlaying) return;
		this.isActuallyPlaying = false;
		this.sound!.pause();
		this.updateTime();
	}
	/**
	 * Handles the hard part of looping the sound
	 */
	updateTime() {
		clearTimeout(this.timer);
		this.timer = undefined;
		if (this !== BattleSound.currentBgm()) return;
		if (!this.sound) return;

		const progress = this.sound.currentTime * 1000;
		if (progress > this.loopend - 1000) {
			this.sound.currentTime -= (this.loopend - this.loopstart) / 1000;
		}

		this.timer = setTimeout(() => {
			this.updateTime();
		}, Math.max(this.loopend - progress, 1));
	}

	static update() {
		const current = BattleSound.currentBgm();
		for (const bgm of BattleSound.bgm) {
			if (bgm.isPlaying) {
				if (bgm === current) {
					bgm.actuallyResume();
				} else {
					bgm.actuallyPause();
				}
			}
		}
	}
}

export const BattleSound = new class {
	soundCache: { [url: string]: HTMLAudioElement | undefined } = {};

	bgm: BattleBGM[] = [];

	// options
	effectVolume = 50;
	bgmVolume = 50;
	muted = false;
	/**
	 * cherish-bgm-title: 지금 배틀에 배정된 곡 제목 (patch-bgm-title.py).
	 * `currentBgm()` 과 달리 **음량 0·음소거·탭 양보와 무관하게** 유지된다 —
	 * 소리가 꺼져 있어도 무슨 곡인지는 알려주기로 했기 때문(사용자 확정).
	 */
	cherishTrackTitle: string | null = null;
	/** cherish-music-cd: 이 곡을 튼 사람 (음악 CD, 2026-09-09). 기본곡·익명 판이면 null. */
	cherishTrackOwner: string | null = null;
	/**
	 * cherish-bgm-title: 지금 배틀에 배정된 곡 제목 (patch-bgm-title.py).
	 * `currentBgm()` 과 달리 **음량 0·음소거·탭 양보와 무관하게** 유지된다 —
	 * 소리가 꺼져 있어도 무슨 곡인지는 알려주기로 했기 때문(사용자 확정).
	 */
	cherishTrackTitle: string | null = null;
	/** cherish-bgm-single: 다른 탭이 재생권을 쥐고 있어 이 탭은 조용히 한다. */
	yieldedToOtherTab = false;
	/** cherish-bgm-single: 재생권 요청 (탭 간 채널이 없으면 undefined). */
	claimBgmTab?: () => void;

	getSound(url: string) {
		if (!window.HTMLAudioElement) return;
		if (this.soundCache[url]) return this.soundCache[url];
		try {
			const sound = document.createElement('audio');
			sound.src = `https://${Config.routes.client}/${url}`;
			sound.volume = this.effectVolume / 100;
			this.soundCache[url] = sound;
			return sound;
		} catch {}
	}

	playEffect(url: string, roomid?: string) {
		if (!extractionAudioAllows(roomid)) return; // cherish-extraction-audio
		// cherish-battle-volume: 효과음·울음소리만 낮춘다. playSound 는 알림음도 쓰므로 건드리지 않는다.
		this.playSound(url, this.muted ? 0 : this.effectVolume * CHERISH_EFFECT_SCALE);
	}

	playSound(url: string, volume: number) {
		if (!extractionAudioAllows()) return; // cherish-extraction-audio
		if (!volume) return;
		const effect = this.getSound(url);
		if (effect) {
			effect.volume = volume / 100;
			effect.play();
		}
	}

	/** loopstart and loopend are in milliseconds */
	loadBgm(url: string, loopstart: number, loopend: number, replaceBGM?: BattleBGM | null) {
		if (replaceBGM) {
			replaceBGM.stop();
			this.deleteBgm(replaceBGM);
		}

		const bgm = new BattleBGM(url, loopstart, loopend);
		this.bgm.push(bgm);
		return bgm;
	}
	deleteBgm(bgm: BattleBGM) {
		const soundIndex = BattleSound.bgm.indexOf(bgm);
		if (soundIndex >= 0) BattleSound.bgm.splice(soundIndex, 1);
	}

	currentBgm() {
		if (!this.bgmVolume || this.muted) return false;
		if (this.yieldedToOtherTab) return false; // cherish-bgm-single
		// cherish-extraction-audio-bgm:begin
		if (extractionAudioEmbed) {
			if (!extractionAudioAllows()) return false;
			const bgm = Object(PS.rooms[extractionAudioRoom as any]).battle?.scene?.bgm;
			return bgm?.isPlaying ? bgm : null;
		}
		// cherish-extraction-audio-bgm:end
		// cherish-bgm-newest (patch-bgm-newest-room.py): 방이 여럿이면 **가장 나중에 튼 곡**이 이긴다.
		// 상류는 앞에서부터 훑어 「먼저 연 방」이 이겼다. 그런데 patch-bgm-keep-after-end.py 로
		// **끝난 방도 isPlaying 을 유지**하고, 구름 미궁은 다음 단계 방을 연 뒤에 끝난 방을 닫는다 —
		// 그 사이 끝난 방이 자리를 쥐어 새 단계가 조용해졌다(곡이 같으면 <audio> 공유라 아예 무음).
		// 「지금 보고 있는 배틀 = 방금 연 배틀」이므로 나중 것이 이기는 게 맞다.
		for (let i = this.bgm.length - 1; i >= 0; i--) {
			const bgm = this.bgm[i];
			if (bgm && bgm.isPlaying) return bgm;
		}
		return null;
	}

	/** cherish-bgm-autoplay: 재시도 리스너가 이미 걸려 있는가. */
	bgmGestureRetryArmed = false;
	/** cherish-bgm-autoplay: 자동재생이 거부된 곡을 첫 클릭·키 입력 때 다시 튼다. */
	retryBgmOnGesture() {
		if (this.bgmGestureRetryArmed || typeof document === 'undefined') return;
		this.bgmGestureRetryArmed = true;
		const retry = () => {
			this.bgmGestureRetryArmed = false;
			document.removeEventListener('pointerdown', retry, true);
			document.removeEventListener('keydown', retry, true);
			BattleBGM.update();
		};
		document.addEventListener('pointerdown', retry, true);
		document.addEventListener('keydown', retry, true);
	}

	// setting
	setMute(muted: boolean) {
		muted = !!muted;
		if (this.muted === muted) return;
		this.muted = muted;
		BattleBGM.update();
	}

	loudnessPercentToAmplitudePercent(loudnessPercent: number) {
		// 10 dB is perceived as approximately twice as loud
		let decibels = 10 * Math.log(loudnessPercent / 100) / Math.log(2);
		return 10 ** (decibels / 20) * 100;
	}
	setBgmVolume(bgmVolume: number) {
		this.bgmVolume = this.loudnessPercentToAmplitudePercent(bgmVolume);
		BattleBGM.update();
	}
	setEffectVolume(effectVolume: number) {
		this.effectVolume = this.loudnessPercentToAmplitudePercent(effectVolume);
	}
};

// cherish-extraction-audio-listen:begin
installExtractionAudio(() => {
	// Pause cached hit sounds as well as looping BGM when the parent hides/switches battle.
	for (const sound of Object.values(BattleSound.soundCache)) sound?.pause();
	for (const bgm of BattleSound.bgm) bgm.actuallyPause();
	BattleBGM.update();
});
// cherish-extraction-audio-listen:end
if (typeof PS === 'object') {
	PS.prefs.subscribeAndRun(key => {
		if (!key || key === 'musicvolume' || key === 'effectvolume' || key === 'mute') {
			BattleSound.effectVolume = PS.prefs.effectvolume;
			BattleSound.bgmVolume = PS.prefs.musicvolume;
			BattleSound.muted = PS.prefs.mute;
			BattleBGM.update();
		}
	});
}

// cherish-bgm-single:begin (patch-bgm-single-tab.py)
/**
 * 탭 간 BGM 단독 재생. 같은 브라우저에서 사이트를 두 탭 열면 클라이언트가 따로 떠서
 * 배틀 BGM 이 겹쳐 들렸다 (2026-09-03 실측). BGM 을 실제로 시작한 탭이 재생권을 쥐고
 * 나머지는 양보한다. 양보한 탭은 화면에 나타나거나 창 포커스를 받을 때(재생 중인 배틀이
 * 있으면) 이어받는다 — 음악은 늘 「내가 보는 배틀」에서 난다. 다른 탭 안내창은 띄우지 않는다.
 * 탭이 하나뿐이면 백그라운드에서도 예전처럼 계속 재생된다.
 */
(() => {
	if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return;
	const tabId = Math.random().toString(36).slice(2);
	let channel: BroadcastChannel;
	try {
		channel = new BroadcastChannel('cherish-bgm');
	} catch {
		return;
	}
	const hasPlayingBgm = () => extractionAudioAllows() && BattleSound.bgm.some(bgm => bgm.isPlaying); // cherish-extraction-audio
	const claim = () => { channel.postMessage({ type: 'claim', tabId }); };
	BattleSound.claimBgmTab = () => {
		if (!extractionAudioAllows()) return; // cherish-extraction-audio
		if (!BattleSound.yieldedToOtherTab) { claim(); return; }
		BattleSound.yieldedToOtherTab = false;
		claim();
		BattleBGM.update();
	};
	channel.onmessage = (ev: MessageEvent) => {
		const msg = (ev.data || {}) as { type?: string, tabId?: string };
		if (msg.tabId === tabId) return;
		if (msg.type === 'claim') {
			if (BattleSound.yieldedToOtherTab) return;
			BattleSound.yieldedToOtherTab = true;
			BattleBGM.update();
		} else if (msg.type === 'release') {
			if (BattleSound.yieldedToOtherTab && !document.hidden && hasPlayingBgm()) BattleSound.claimBgmTab!();
		}
	};
	// 보이게 되거나(탭 전환) 포커스를 받으면(창 두 개를 오갈 때는 visibilitychange 가 안 온다)
	// 재생 중인 배틀이 있는 한 음악을 되찾는다 — 「내가 보는 배틀에서 음악이 나온다」 (2026-09-05).
	const reclaimIfWatching = () => {
		if (!document.hidden && BattleSound.yieldedToOtherTab && hasPlayingBgm()) BattleSound.claimBgmTab!();
	};
	document.addEventListener('visibilitychange', reclaimIfWatching);
	window.addEventListener('focus', reclaimIfWatching);
	window.addEventListener('pagehide', () => {
		if (!BattleSound.yieldedToOtherTab) channel.postMessage({ type: 'release', tabId });
	});
})();
// cherish-bgm-single:end
